import React, { useEffect, useMemo, useState } from 'react';

import defaultStyles from './Media.module.scss';
import VideoPlayer from '../../atoms/VideoPlayer';
import { Image } from '../../atoms/image/Image';
import mergeClasses from '../../../utils/shallowMerge';
import { useDevicesScreen } from '../../../utils/useDevicesScreen';
import { Pause, Play, PlayFilled } from '../../atoms/icons';
import PlayerControls from '../../organisms/customVideoControls/PlayerControls';
import { theme } from './_vars';
import clsx from 'clsx';

const Media = ({
  media,
  customClasses,
  isSSR,
  showControls,
  muted = true,
  wrappedContent,
}) => {
  if (isSSR || !media) return null;

  const [isPlaying, setPlaying] = useState(false);
  const styles = mergeClasses(defaultStyles, customClasses);

  const { isMobile, isTablet } = useDevicesScreen();

  const getMedia = () => {
    if (isMobile && (media?.mobile?.url || media.mobileCloudinary)) {
      return media.mobileCloudinary || media.mobile;
    }

    if (isTablet && (media?.tablet?.url || media.tabletCloudinary)) {
      return media.tabletCloudinary || media.tablet;
    }

    return (
      media.desktopCloudinary ||
      media.desktop ||
      media.tabletCloudinary ||
      media.tablet ||
      media.mobileCloudinary ||
      media.mobile
    );
  };

  const currentMedia = getMedia();

  if (!currentMedia) return null;

  useEffect(() => {
    setPlaying(muted)
  }, [muted])

  const controls = useMemo(() => {
    if (!showControls) return null;

    return (
      <div
        className={clsx(styles.controlsWrapper, {
          [`${styles.controls}`]: isPlaying,
        })}
      >
        <PlayerControls
          playIcon={() => (
            <div className={styles.videoControlIcon}>
              {theme === 'saba' ? (
                <PlayFilled className={styles.playIcon} />
              ) : (
                <Play />
              )}
            </div>
          )}
          pauseIcon={() => (
            <div className={styles.videoControlIcon}>
              <Pause />
            </div>
          )}
          onPlayPause={(ev) => {
            ev.preventDefault();
            setPlaying((prev) => !prev);
          }}
          playing={isPlaying}
        />
      </div>
    )
  }, [isPlaying, showControls]);

  const content =
    currentMedia?.type?.includes('video') ||
    currentMedia.resource_type === 'video' ? (
      <VideoPlayer
        url={currentMedia.url}
        loop
        muted={muted}
        autoplay={true}
        playing={showControls ? isPlaying : true}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        customClasses={{ wrapper: styles.videoWrapper }}
        customControlsComponent={controls}
        style={{
          display: 'flex',
          aspectRatio: currentMedia?.aspectRatio?.replace(':', '/') || 'initial',
        }}
      />
    ) : (
      <Image
        style={{ aspectRatio: currentMedia?.aspectRatio?.replace(':', '/') || 'initial' }}
        alt={currentMedia?.title}
        title={currentMedia?.title}
        itemProp="image"
        className={styles.image}
        image={currentMedia?.resource_type
          ? { ...currentMedia, url: currentMedia.secure_url || currentMedia.url }
          : currentMedia.url
      }
      />
    );

  return wrappedContent ? <div className={styles.media}>{content}</div> : content;
};

export default Media;
